import { useEffect, useState } from 'react'
import { Button } from '@/common/ui/button'
import { Dialog, DialogContent } from '@/common/ui/dialogMobileSupport'
import { PWA_APP_COPY } from '@/locales/en'
import { AlertCircle, CheckCircle2, ScanFace } from 'lucide-react'
import SetupPasskeySubmittedAnalytics from '@/common/analytics/events/pwa/SetupPasskeySubmittedAnalytics'
import SetupPasskeyFailedAnalytics from '@/common/analytics/events/pwa/SetupPasskeyFailedAnalytics'
import SetupPasskeySkippedAnalytics from '@/common/analytics/events/pwa/SetupPasskeySkippedAnalytics'
import PasskeyAuthSubmittedAnalytics from '@/common/analytics/events/pwa/PasskeyAuthSubmittedAnalytics'
import PasskeyAuthFailedAnalytics from '@/common/analytics/events/pwa/PasskeyAuthFailedAnalytics'
import PasskeyAuthLogoutAnalytics from '@/common/analytics/events/pwa/PasskeyAuthLogoutAnalytics'
import { H2 } from '../ui/h2'

export function PasskeySetupRequiredModal(props: {
    open: boolean
    onSetup: () => void
    onSkip: () => void
    error?: string | null
}) {
    const { open, onSetup, onSkip, error } = props

    useEffect(() => {
        if (open && error) {
            SetupPasskeyFailedAnalytics.track({ error })
        }
    }, [open, error])

    useEffect(() => {
        if (open) {
            // Pushing the change to the end of the call stack
            const timer = setTimeout(() => {
                document.body.style.pointerEvents = ''
            }, 0)

            return () => clearTimeout(timer)
        } else {
            document.body.style.pointerEvents = 'auto'
        }
    }, [open])

    return (
        <Dialog open={open}>
            <DialogContent className="w-full h-full px-6 py-8">
                <div className="flex flex-col min-h-0 h-full">
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-full max-w-md flex flex-col items-start gap-6">
                            <ScanFace className="w-9 h-9 relative overflow-hidden" />
                            <div className="flex flex-col items-start gap-4">
                                <H2 className="text-brand-forest text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                                    {PWA_APP_COPY.setup.title}
                                </H2>
                                <div className="text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                                    {PWA_APP_COPY.setup.description}
                                </div>
                            </div>
                            <div className="flex flex-col justify-start items-start gap-4">
                                {PWA_APP_COPY.setup.benefits.map((benefit, i) => (
                                    <div key={i} className="inline-flex justify-start items-center gap-4">
                                        <CheckCircle2
                                            className="ml-2 mr-1 text-sentiment-good w-4 h-4 flex-shrink-0"
                                            style={{ strokeWidth: 3 }}
                                        />
                                        <div className="justify-start text-content-primary text-sm font-normal font-['Inter'] leading-5">
                                            {benefit}{' '}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {error ? <p className="text-sm text-red-600">{error}</p> : null}
                        </div>
                    </div>
                    <div className="mt-auto self-stretch flex flex-col gap-2.5">
                        <Button
                            variant="secondary"
                            fullWidth
                            onClick={() => {
                                SetupPasskeySubmittedAnalytics.track()
                                onSetup()
                            }}
                        >
                            {PWA_APP_COPY.setup.setupButton}
                        </Button>
                        <Button
                            variant="tertiary"
                            fullWidth
                            onClick={() => {
                                SetupPasskeySkippedAnalytics.track()
                                onSkip()
                            }}
                        >
                            {PWA_APP_COPY.setup.skipButton}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export function PasskeyAuthRequiredModal(props: {
    open: boolean
    onUsePasskey: () => void
    onLogout: () => void
    error?: string | null
    waiting?: boolean
}) {
    const { open, onUsePasskey, onLogout, error, waiting } = props
    const [logoutLoading, setLogoutLoading] = useState(false)

    useEffect(() => {
        if (open) {
            setLogoutLoading(false)
            // Pushing the change to the end of the call stack
            const timer = setTimeout(() => {
                document.body.style.pointerEvents = ''
            }, 0)

            return () => clearTimeout(timer)
        } else {
            document.body.style.pointerEvents = 'auto'
        }
    }, [open])

    useEffect(() => {
        if (open && error) {
            PasskeyAuthFailedAnalytics.track({ error })
        }
    }, [open, error])

    return (
        <Dialog open={open}>
            <DialogContent className="w-full h-full px-6 py-8">
                <div className={'flex flex-col min-h-0 h-full ' + (waiting ? 'pointer-events-none select-none' : '')}>
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-full max-w-md flex flex-col items-start gap-6">
                            {error ? (
                                <AlertCircle className="w-9 h-9 relative overflow-hidden" />
                            ) : (
                                <ScanFace className="w-9 h-9 relative overflow-hidden" />
                            )}
                            <div className="flex flex-col items-start gap-4">
                                <H2 className="text-brand-forest text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                                    {error ? PWA_APP_COPY.authError.title : PWA_APP_COPY.authRequired.title}
                                </H2>
                                <div className="text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                                    {error ? PWA_APP_COPY.authError.description : PWA_APP_COPY.authRequired.description}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-auto self-stretch flex flex-col gap-2.5">
                        <Button
                            variant="secondary"
                            fullWidth
                            onClick={() => {
                                PasskeyAuthSubmittedAnalytics.track()
                                onUsePasskey()
                            }}
                            disabled={logoutLoading}
                            loading={!!waiting}
                        >
                            {PWA_APP_COPY.authRequired.usePasskeyButton}
                        </Button>
                        <Button
                            variant="tertiary"
                            fullWidth
                            onClick={() => {
                                PasskeyAuthLogoutAnalytics.track()
                                setLogoutLoading(true)
                                onLogout()
                            }}
                            loading={logoutLoading}
                            disabled={!!waiting || logoutLoading}
                        >
                            {PWA_APP_COPY.authRequired.logoutButton}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
